import { useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";

import X from "chameleon/icons/x";
import { Button, Text } from "chameleon/lib/components";

import ModalPortal from "./modal-portal";
import {
  modal,
  modalCloseBtn,
  modalContent,
  modalEnter,
  modalEnterActive,
  modalEnterDone,
  modalExit,
  modalExitActive,
  modalExitDone,
  modalHeader,
  modalHeaderContentRight,
  modalTitle,
} from "./modal.css";

interface ModalProps {
  wrapperId: string;
  children: JSX.Element | string;
  isModalOpen: boolean | undefined;
  title: string;
  closeModalHandler: () => void;
  width?: string;
  maxWidth?: string;
  height?: string;
  fullScreen?: boolean;
}

export const Modal = ({
  wrapperId,
  children,
  isModalOpen,
  title,
  closeModalHandler,
  height,
  width,
  maxWidth,
  fullScreen,
}: ModalProps) => {
  const nodeRef = useRef(null);

  return (
    <ModalPortal wrapperId={wrapperId}>
      <CSSTransition
        in={isModalOpen}
        timeout={{ enter: 600, exit: 600 }}
        unmountOnExit
        classNames={{
          enter: modalEnter,
          enterActive: modalEnterActive,
          enterDone: modalEnterDone,
          exit: modalExit,
          exitActive: modalExitActive,
          exitDone: modalExitDone,
        }}
        nodeRef={nodeRef}
      >
        <div data-testid="modal" className={modal} ref={nodeRef}>
          <ModalContent
            fullScreen={fullScreen}
            width={width}
            height={height}
            maxWidth={maxWidth}
            title={title}
            closeModalHandler={closeModalHandler}
          >
            {children}
          </ModalContent>
        </div>
      </CSSTransition>
    </ModalPortal>
  );
};

interface ModalContentProps {
  fullScreen?: boolean;
  children: JSX.Element | string;
  width?: string;
  maxWidth?: string;
  height?: string;
  title?: string;
  closeModalHandler: () => void;
}
const ModalContent = ({
  children,
  fullScreen = false,
  width,
  maxWidth,
  height,
  title,
  closeModalHandler,
}: ModalContentProps) => {
  useEffect(() => {
    const closeOnEscapeKey = (e: KeyboardEvent) =>
      e.key === "Escape" ? closeModalHandler() : null;
    document.body.addEventListener("keydown", closeOnEscapeKey);
    return () => {
      document.body.removeEventListener("keydown", closeOnEscapeKey);
    };
  }, [closeModalHandler]);

  const getModalStyle = () => {
    const style = {
      height: height || "50vw",
      width: width || "50vw",
      maxWidth: maxWidth || "98%",
    };
    return style;
  };

  return fullScreen ? (
    <>{children}</>
  ) : (
    <>
      <div
        className={modalHeader}
        style={{
          width: getModalStyle().width,
          maxWidth: getModalStyle().maxWidth,
        }}
      >
        <div className={modalTitle}>
          <Text styleVariant="heading1">{title}</Text>
        </div>
        <nav id="closeBtn" className={modalHeaderContentRight}>
          <div className={modalCloseBtn}>
            <Button
              id="close-modal-btn"
              text={""}
              svg={<X sizeVariant={32} role="presentation" />}
              onClick={() => {
                closeModalHandler();
              }}
            />
          </div>
        </nav>
      </div>
      <div className={modalContent} style={getModalStyle()}>
        {children}
      </div>
    </>
  );
};
